
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Flight } from '@/types/flight';
import { useToast } from '@/components/ui/use-toast';

interface FlightUpdate {
  id: string;
  flight_id: string;
  organization_id: string;
  updated_by: string;
  update_type: string;
  previous_values: any;
  new_values: any;
  change_summary: string;
  created_at: string;
}

export function useFlightUpdates() {
  const [updates, setUpdates] = useState<FlightUpdate[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    // Subscribe to flight updates log
    const updatesChannel = supabase
      .channel('flight-updates-log')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'flight_updates_log'
        },
        (payload) => {
          console.log('New flight update logged:', payload);
          const newUpdate = payload.new as FlightUpdate;
          setUpdates(prev => [newUpdate, ...prev]);
          
          // Show toast notification for real-time updates
          toast({
            title: "Flight Update",
            description: newUpdate.change_summary,
          });
        }
      )
      .subscribe();

    // Subscribe to flight table changes for real-time board updates
    const flightsChannel = supabase
      .channel('flights-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'flights'
        },
        (payload) => {
          console.log('Flight updated:', payload);
          // This will trigger a re-fetch in components using react-query
          // The flight board will automatically update
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(updatesChannel);
      supabase.removeChannel(flightsChannel);
    };
  }, [toast]);

  const fetchUpdates = async () => {
    try {
      const { data, error } = await supabase
        .from('flight_updates_log')
        .select(`
          *,
          flights!inner(flight_number, organization:organizations(name))
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setUpdates(data || []);
    } catch (error: any) {
      console.error('Error fetching flight updates:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch flight updates.",
      });
    }
  };

  useEffect(() => {
    fetchUpdates();
  }, []);

  return { updates, fetchUpdates };
}
